import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { complaintsApi } from '../../services/adminApi'

interface Complaint {
  id: number
  student_id: number
  teacher_id: number
  booking_id: number
  complaint_type: string
  complaint_reason: string
  evidence_urls: string[] | null
  status: string
  priority: string
  student_name: string
  teacher_name: string
  scheduled_start: string
  scheduled_end: string
  material_name?: string
  student_paid_amount?: number
  resolution?: string
  admin_notes?: string
  resolved_at?: string
  created_at: string
  updated_at: string
}

const ComplaintsManagement: React.FC = () => {
  const [complaints, setComplaints] = useState<Complaint[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [resolutionType, setResolutionType] = useState<'teacher_fault' | 'student_fault' | ''>('')
  const [resolutionNotes, setResolutionNotes] = useState('')
  const [resolving, setResolving] = useState(false)

  useEffect(() => {
    loadComplaints()
  }, [statusFilter])

  const loadComplaints = async () => {
    setLoading(true)
    try {
      const response = await complaintsApi.getComplaints(statusFilter || undefined)
      if (response.success && response.data) {
        // 解析 evidence_urls
        const parsedComplaints = response.data.map((c: any) => ({
          ...c,
          evidence_urls: typeof c.evidence_urls === 'string' ? JSON.parse(c.evidence_urls) : c.evidence_urls
        }))
        setComplaints(parsedComplaints)
      } else {
        setComplaints([])
      }
    } catch (error) {
      console.error('加载投诉列表失败:', error)
      toast.error('加载投诉列表失败')
      setComplaints([])
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'processing': return 'bg-blue-100 text-blue-800'
      case 'resolved': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待处理'
      case 'investigating': return '调查中'
      case 'processing': return '处理中'
      case 'resolved': return '已解决'
      case 'rejected': return '已拒绝'
      default: return status
    }
  }

  const getComplaintTypeText = (type: string) => {
    switch (type) {
      case 'service_issue': return '服务问题'
      case 'quality_issue': return '教学质量问题'
      case 'attitude_issue': return '态度问题'
      case 'late_issue': return '迟到问题'
      case 'absent_issue': return '缺席问题'
      case 'payment_issue': return '支付问题'
      case 'other': return '其他问题'
      default: return type
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'normal': return 'bg-blue-100 text-blue-800'
      case 'low': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'urgent': return '紧急'
      case 'high': return '高'
      case 'normal': return '普通'
      case 'low': return '低'
      default: return priority
    }
  }

  const handleStatusUpdate = async (complaintId: number, newStatus: string) => {
    try {
      const response = await complaintsApi.updateComplaintStatus(complaintId.toString(), newStatus)
      if (response.success) {
        toast.success('状态更新成功')
        loadComplaints()
      } else {
        toast.error(response.error?.message || '更新失败')
      }
    } catch (error) {
      console.error('更新投诉状态失败:', error)
      toast.error('更新投诉状态失败')
    }
  }

  // 打开处理投诉弹窗
  const handleOpenResolve = async (complaint: Complaint) => {
    setSelectedComplaint(complaint)
    setResolutionType('')
    setResolutionNotes('')
    // 如果投诉没有费用信息，先获取详情
    if (!complaint.student_paid_amount) {
      try {
        const response = await complaintsApi.getComplaintDetail(complaint.id.toString())
        if (response.success && response.data?.complaint) {
          const detailedComplaint = {
            ...complaint,
            student_paid_amount: response.data.complaint.student_paid_amount
          }
          setSelectedComplaint(detailedComplaint)
        }
      } catch (error) {
        console.error('获取投诉详情失败:', error)
      }
    }
    setShowResolveModal(true)
  }

  // 处理投诉并执行财务操作
  const handleResolveComplaint = async () => {
    if (!selectedComplaint || !resolutionType) {
      toast.error('请选择处理方式')
      return
    }

    setResolving(true)
    try {
      const response = await complaintsApi.resolveComplaint(
        selectedComplaint.id.toString(),
        {
          resolution_type: resolutionType,
          notes: resolutionNotes || undefined
        }
      )

      if (response.success) {
        toast.success('投诉处理完成')
        setShowResolveModal(false)
        setShowDetailModal(false)
        loadComplaints()
      } else {
        toast.error(response.error?.message || '处理失败')
      }
    } catch (error) {
      console.error('处理投诉失败:', error)
      toast.error('处理投诉失败')
    } finally {
      setResolving(false)
    }
  }

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('zh-CN')
  }

  // 搜索过滤
  const filteredComplaints = complaints.filter(c => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      c.student_name?.toLowerCase().includes(term) ||
      c.teacher_name?.toLowerCase().includes(term) ||
      c.complaint_reason?.toLowerCase().includes(term)
    )
  })

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">投诉管理</h1>
        <p className="text-gray-600">处理用户投诉和纠纷</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium">投诉列表 ({filteredComplaints.length})</h2>
            <div className="flex space-x-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">所有状态</option>
                <option value="pending">待处理</option>
                <option value="investigating">调查中</option>
                <option value="resolved">已解决</option>
                <option value="rejected">已拒绝</option>
              </select>
              <input
                type="text"
                placeholder="搜索投诉..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={loadComplaints}
                className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                刷新
              </button>
            </div>
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-gray-500">加载中...</p>
            </div>
          ) : filteredComplaints.length === 0 ? (
            <div className="text-center py-8 text-gray-500">暂无投诉</div>
          ) : (
            <div className="space-y-4">
              {filteredComplaints.map((complaint) => (
                <div key={complaint.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {getComplaintTypeText(complaint.complaint_type)}
                      </h3>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${getPriorityColor(complaint.priority)}`}>
                        优先级: {getPriorityText(complaint.priority)}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(complaint.status)}`}>
                      {getStatusText(complaint.status)}
                    </span>
                  </div>

                  <p className="text-gray-600 mb-3 line-clamp-2">{complaint.complaint_reason}</p>

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-500 mb-3">
                    <div>
                      <span className="font-medium">投诉学生：</span> {complaint.student_name || '-'}
                    </div>
                    <div>
                      <span className="font-medium">被投诉教师：</span> {complaint.teacher_name || '-'}
                    </div>
                    <div>
                      <span className="font-medium">课程时间：</span> {formatDateTime(complaint.scheduled_start)}
                    </div>
                    <div>
                      <span className="font-medium">教材：</span> {complaint.material_name || '-'}
                    </div>
                    <div>
                      <span className="font-medium">创建时间：</span> {formatDateTime(complaint.created_at)}
                    </div>
                  </div>

                  {/* 证据截图 */}
                  {complaint.evidence_urls && complaint.evidence_urls.length > 0 && (
                    <div className="mb-3">
                      <span className="text-sm font-medium text-gray-700">证据图片：</span>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {complaint.evidence_urls.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                            <img src={url} alt={`证据${idx + 1}`} className="w-16 h-16 object-cover rounded border hover:opacity-80" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <button
                      onClick={() => { setSelectedComplaint(complaint); setShowDetailModal(true); }}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      查看详情
                    </button>
                    {(complaint.status === 'pending' || complaint.status === 'investigating') && (
                      <>
                        <button
                          onClick={() => handleOpenResolve(complaint)}
                          className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                        >
                          处理投诉
                        </button>
                        {complaint.status === 'pending' && (
                          <button
                            onClick={() => handleStatusUpdate(complaint.id, 'investigating')}
                            className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                          >
                            开始调查
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 投诉详情弹窗 */}
      {showDetailModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  投诉详情 #{selectedComplaint.id}
                </h3>
                <button
                  onClick={() => { setShowDetailModal(false); setSelectedComplaint(null); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-sm rounded-full ${getStatusColor(selectedComplaint.status)}`}>
                    {getStatusText(selectedComplaint.status)}
                  </span>
                  <span className={`px-2 py-1 text-sm rounded-full ${getPriorityColor(selectedComplaint.priority)}`}>
                    {getPriorityText(selectedComplaint.priority)}
                  </span>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-1">投诉类型</h4>
                  <p className="text-gray-900">{getComplaintTypeText(selectedComplaint.complaint_type)}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-1">投诉原因</h4>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedComplaint.complaint_reason}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">投诉学生</h4>
                    <p className="text-gray-900">{selectedComplaint.student_name || '-'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">被投诉教师</h4>
                    <p className="text-gray-900">{selectedComplaint.teacher_name || '-'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">课程时间</h4>
                    <p className="text-gray-900">{formatDateTime(selectedComplaint.scheduled_start)}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">使用教材</h4>
                    <p className="text-gray-900">{selectedComplaint.material_name || '-'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">提交时间</h4>
                    <p className="text-gray-900">{formatDateTime(selectedComplaint.created_at)}</p>
                  </div>
                  {selectedComplaint.student_paid_amount && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-1">课程费用</h4>
                      <p className="text-gray-900 font-semibold text-lg">¥{parseFloat(selectedComplaint.student_paid_amount.toString()).toFixed(2)}</p>
                    </div>
                  )}
                </div>

                {/* 证据图片 */}
                {selectedComplaint.evidence_urls && selectedComplaint.evidence_urls.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">证据图片</h4>
                    <div className="flex gap-2 flex-wrap">
                      {selectedComplaint.evidence_urls.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt={`证据${idx + 1}`} className="w-24 h-24 object-cover rounded border hover:opacity-80" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* 处理结果 */}
                {selectedComplaint.resolution && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">处理结果</h4>
                    <p className="text-gray-900">{selectedComplaint.resolution}</p>
                    {selectedComplaint.resolved_at && (
                      <p className="text-sm text-gray-500 mt-1">处理时间：{formatDateTime(selectedComplaint.resolved_at)}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                {(selectedComplaint.status === 'pending' || selectedComplaint.status === 'investigating') && (
                  <button
                    onClick={() => { setShowDetailModal(false); handleOpenResolve(selectedComplaint); }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    处理投诉
                  </button>
                )}
                <button
                  onClick={() => { setShowDetailModal(false); setSelectedComplaint(null); }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 处理投诉弹窗 */}
      {showResolveModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  处理投诉 #{selectedComplaint.id}
                </h3>
                <button
                  onClick={() => { setShowResolveModal(false); setResolutionType(''); setResolutionNotes(''); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* 课程信息 */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">课程信息</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-600">学生：</span> {selectedComplaint.student_name}</p>
                    <p><span className="text-gray-600">教师：</span> {selectedComplaint.teacher_name}</p>
                    <p><span className="text-gray-600">课程时间：</span> {formatDateTime(selectedComplaint.scheduled_start)}</p>
                    {selectedComplaint.student_paid_amount && (
                      <p className="font-semibold text-lg mt-2">
                        <span className="text-gray-600">课程费用：</span>
                        <span className="text-blue-600">¥{parseFloat(selectedComplaint.student_paid_amount.toString()).toFixed(2)}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* 责任判定 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    责任判定 <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="resolutionType"
                        value="teacher_fault"
                        checked={resolutionType === 'teacher_fault'}
                        onChange={(e) => setResolutionType(e.target.value as 'teacher_fault')}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">教师责任</div>
                        <div className="text-sm text-gray-600">
                          将退款给学生，取消教师收益
                          {selectedComplaint.student_paid_amount && (
                            <span className="text-red-600 font-medium">（退款金额：¥{parseFloat(selectedComplaint.student_paid_amount.toString()).toFixed(2)}）</span>
                          )}
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="resolutionType"
                        value="student_fault"
                        checked={resolutionType === 'student_fault'}
                        onChange={(e) => setResolutionType(e.target.value as 'student_fault')}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">学生责任</div>
                        <div className="text-sm text-gray-600">
                          恢复教师收益正常冻结期（14天后可提现）
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* 处理备注 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    处理备注
                  </label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="请输入处理备注（可选）"
                  />
                </div>

                {/* 警告信息 */}
                {resolutionType && (
                  <div className={`p-3 rounded-lg ${resolutionType === 'teacher_fault' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
                    }`}>
                    <p className="text-sm font-medium">
                      {resolutionType === 'teacher_fault' ? '⚠️ 警告' : '✅ 提示'}
                    </p>
                    <p className="text-sm mt-1">
                      {resolutionType === 'teacher_fault'
                        ? '此操作将立即退款给学生并取消教师收益，不可逆！'
                        : '此操作将恢复教师收益的正常冻结期，到期后教师可以提现。'
                      }
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  onClick={() => { setShowResolveModal(false); setResolutionType(''); setResolutionNotes(''); }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                  disabled={resolving}
                >
                  取消
                </button>
                <button
                  onClick={handleResolveComplaint}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={!resolutionType || resolving}
                >
                  {resolving ? '处理中...' : '确认处理'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { ComplaintsManagement }
export default ComplaintsManagement